import React, { useEffect, useRef, useState } from "react";
import Fuse from "fuse.js";

import { Button } from "Components/atoms/Button";
import { DropdownMobile } from "Components/atoms/Dropdown/Mobile";
import { InputFieldMobile } from "Components/atoms/InputField/Mobile";

import { Airport } from "shared/types/airportsTypes";
import {
  FieldSearchContentMobile,
  FieldSearchDropdownMobile,
  FieldSearchHeaderMobile,
  FieldSearchMobile,
  ModalBackground,
} from "./styles";
import { InputFieldSearchMobileProps } from "./types";
// @ts-ignore
import { ReactComponent as PlaneGoingSvg } from "assets/svgs/plane-going.svg";
import { ReactComponent as PlaneBackSvg } from "assets/svgs/plane-back.svg";
import { FaArrowLeft } from "react-icons/fa";
import { removeAccents } from "shared/utils/removeAccents";
import airportsData from "shared/config/airports/AllAirports/airports.json";
import { useGeneralContext } from "shared/contexts/GeneralContext";
import { useSearchContext } from "shared/contexts/SearchContext";

/**
 * Componente InputFieldSearchMobile (Mobile)
 *
 * Barra de busca para selecionar origens no layout Mobile com autocomplete.
 */
export const InputFieldSearchMobile: React.FC<InputFieldSearchMobileProps> = ({
  origin,
  origins,
  destination,
  nationalDestinations,
  internationalDestinations,
  isVisible,
  ref,
  setIsVisible,
  onSelect,
  onSearchChange,
  type,
}) => {
  const [filteredOrigins, setFilteredOrigins] = useState<Airport[]>(
    origins || [],
  );
  const [isSearchTermTyped, setSearchTermTyped] = useState<boolean>(false);
  const [filteredNationalDestinations, setFilteredNationalDestinations] =
    useState<Airport[]>(nationalDestinations || []);
  const [
    filteredInternationalDestinations,
    setFilteredInternationalDestinations,
  ] = useState<Airport[]>(internationalDestinations || []);
  const [query, setQuery] = useState("");
  const {
    setIsHeaderVisible,
    setIsOriginModalVisible,
    setIsDestionationModalVisible,
  } = useGeneralContext();

  const { setOrigin, setDestination } = useSearchContext();

  useEffect(() => {
    setIsHeaderVisible(!isVisible);
  }, [isVisible, setIsHeaderVisible]);

  const inputRef = useRef<HTMLInputElement>(null);

  const fuse = new Fuse(airportsData, {
    keys: ["city_name", "airport_name", "airport_code"],
    threshold: 0.3,
    includeScore: true,
  });

  const handleAutoCompleteOrigin = (value: string) => {
    setQuery(value);
    if (value.trim().length > 0) {
      const searchTerm = removeAccents(value.trim()).toLowerCase();
      const results = fuse.search(searchTerm);

      const formattedResults: Airport[] = results.map((result) => ({
        name: result.item.city_name,
        code: result.item.airport_code,
        description: `${result.item.city_name} - ${result.item.airport_name}, ${result.item.country_name}`,
        // destak: result.item.is_metropolitan_area === 1,
      }));

      const uniqueResults = formattedResults.filter(
        (obj, index, self) =>
          self.findIndex((o) => o.code === obj.code) === index,
      );

      setFilteredOrigins(uniqueResults);
      setSearchTermTyped(true);
      onSearchChange?.(value, true, uniqueResults);
    } else {
      resetOrigins();
      onSearchChange?.("", false, []);
    }
  };

  const handleAutoCompleteDestination = (value: string) => {
    setQuery(value);
    if (value.trim().length > 0) {
      const searchTerm = removeAccents(value.trim()).toLowerCase();
      const results = fuse.search(searchTerm);

      const formattedResults: Airport[] = results.map((result) => ({
        name: result.item.city_name,
        code: result.item.airport_code,
        description: `${result.item.city_name} - ${result.item.airport_name}, ${result.item.country_name}`,
        // destak: result.item.is_metropolitan_area === 1,
        countryCode: result.item.country_code,
      }));

      const uniqueResults = formattedResults.filter(
        (obj, index, self) =>
          self.findIndex((o) => o.code === obj.code) === index,
      );

      setSearchTermTyped(true);

      setFilteredNationalDestinations(
        uniqueResults.filter((result) => result.countryCode === "BR"),
      );

      setFilteredInternationalDestinations(
        uniqueResults.filter((result) => result.countryCode !== "BR"),
      );
    } else {
      resetDestinations();
    }
  };

  const resetOrigins = () => {
    setFilteredOrigins([...(origins || [])]);
    setOrigin({ code: "", name: "", description: "" });
    setSearchTermTyped(false);
  };

  const resetDestinations = () => {
    setFilteredNationalDestinations(nationalDestinations || []);
    setFilteredInternationalDestinations(internationalDestinations || []);
    setDestination({ code: "", name: "", description: "" });
  };

  useEffect(() => {
    setQuery(origin || "");
  }, [origin]);

  useEffect(() => {
    setQuery(destination || "");
  }, [destination]);

  useEffect(() => {
    onSearchChange?.(query, isSearchTermTyped, filteredOrigins);
  }, [query, isSearchTermTyped, filteredOrigins, onSearchChange]);

  return (
    <FieldSearchContentMobile isVisible={isVisible} ref={ref}>
      <FieldSearchHeaderMobile isVisible={isVisible}>
        <FieldSearchMobile>
          {type === "origin" ? <PlaneGoingSvg /> : <PlaneBackSvg />}
          <InputFieldMobile
            ref={inputRef}
            value={origin || query}
            onFocus={() => {
              if (type === "origin") {
                resetOrigins();
                setIsOriginModalVisible(true);
              } else {
                resetDestinations();
                setIsDestionationModalVisible(true);
              }
            }}
            onChange={(e) => {
              type === "origin"
                ? handleAutoCompleteOrigin(e.target.value)
                : handleAutoCompleteDestination(e.target.value);
            }}
            onClear={() => {
              setQuery("");
              type === "origin" ? resetOrigins() : resetDestinations();
              setSearchTermTyped(false);
              setIsVisible(true);
              inputRef.current?.select();
            }}
            placeholder={
              type === "origin" ? "Partindo de onde?" : "Indo para onde?"
            }
            aria-label={
              type === "origin"
                ? "Campo de busca de origem"
                : "Campo de busca de destino"
            }
          />
        </FieldSearchMobile>
      </FieldSearchHeaderMobile>

      {/* LISTA DE AEROPORTOS */}
      <FieldSearchDropdownMobile type={type}>
        {isVisible && type === "origin" ? (
          <DropdownMobile
            isVisible={isVisible}
            onSelect={(selected) => {
              onSelect(selected as Airport);
              setQuery(selected.description);
            }}
            suggestions={{
              title: isSearchTermTyped ? "" : "Origens mais buscadas",
              type: type,
              nationals: filteredOrigins,
              onSelect,
            }}
            isSearchTermTyped={isSearchTermTyped}
          />
        ) : (
          <DropdownMobile
            isVisible={isVisible}
            onSelect={(selected) => {
              onSelect(selected as Airport);
              setQuery(selected.description);
            }}
            suggestions={{
              title: isSearchTermTyped ? "" : "Destinos mais buscados",
              type: "destination",
              nationalTitle: "Nacionais",
              internationalTitle: "Internacionais",
              nationals: filteredNationalDestinations,
              internationals: filteredInternationalDestinations,
              onSelect,
            }}
            isSearchTermTyped={isSearchTermTyped}
          />
        )}

        {/* {isVisible && } */}
      </FieldSearchDropdownMobile>
    </FieldSearchContentMobile>
  );
};
